import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "./CartContext";
import { Navbar, Footer, CartSheet } from "./components";
import { Home, Services, Portfolio, About, ServiceAreas, Contact, Consult, Paints, Blog, BlogPost } from "./pages";
import { Shop, ShopCategory, CheckoutSuccess, Admin } from "./shop";
import { PaintCalculator } from "./calculator";
import { ColorQuiz } from "./quiz";
import { ColorSearch } from "./colorsearch";
import { SheenQuiz } from "./sheen";
import { FreeTools } from "./freetools";
import { AuthProvider, AuthCallback, Login, Signup, ForgotPassword, ResetPassword, VerifyEmail } from "./auth";
import { PortalLayout, PortalOverview, PortalHomes, PortalQuizResults, PortalSavedColors, PortalSavedPalettes, PortalEstimates, PortalPurchases, PortalAccount, PortalPaintHistory, PortalReferrals } from "./portal";
import { Refer } from "./refer";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously handle Emergent Auth session_id in URL fragment BEFORE any other routing
  // so we never race the /auth/me check when Google callback lands on /portal.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollTop />
      <Navbar />
      <CartSheet />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/about" element={<About />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/courses" element={<ShopCategory type="course" title="Color Selection Courses" subtitle="Designer-led video courses that teach you to choose color like a professional, using the exact framework we use on luxury estates." />} />
          <Route path="/renderings" element={<ShopCategory type="rendering" title="Project Renderings" subtitle="Photorealistic renderings of your space in your chosen colors, so you can see the finished result before a single brushstroke." />} />
          <Route path="/consult" element={<Consult />} />
          <Route path="/paints" element={<Paints />} />
          <Route path="/paint-calculator" element={<PaintCalculator />} />
          <Route path="/color-quiz" element={<ColorQuiz />} />
          <Route path="/sheen-quiz" element={<SheenQuiz />} />
          <Route path="/colors" element={<ColorSearch />} />
          <Route path="/free-tools" element={<FreeTools />} />
          <Route path="/palettes" element={<ShopCategory type="palette" title="Designer-Curated Palettes" subtitle="Go beyond a single color. Each designer-curated palette is a complete, coordinated color story: wall, trim, ceiling, cabinet and accent colors with verified paint codes, finish recommendations and personal designer notes." />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/refer" element={<Refer />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/referrals" element={<Admin />} />
          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          {/* Portal (protected inside PortalLayout) */}
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalOverview />} />
            <Route path="homes" element={<PortalHomes />} />
            <Route path="paint-history" element={<PortalPaintHistory />} />
            <Route path="quiz-results" element={<PortalQuizResults />} />
            <Route path="saved-colors" element={<PortalSavedColors />} />
            <Route path="saved-palettes" element={<PortalSavedPalettes />} />
            <Route path="estimates" element={<PortalEstimates />} />
            <Route path="purchases" element={<PortalPurchases />} />
            <Route path="referrals" element={<PortalReferrals />} />
            <Route path="account" element={<PortalAccount />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function Shell() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return <AppRouter />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
